/**
 * List of UTM parameters to track
 */
const URL_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'coupon'];

/**
 * Sanitizes a parameter value by removing control characters and encoding special characters
 * to prevent XSS attacks.
 * @param {string} value - The parameter value to sanitize
 * @returns {string} - The sanitized parameter value
 */
function sanitizeParamValue(value) {
  if (!value) return '';

  try {
    // First decode the value in case it's URL encoded
    const decodedValue = decodeURIComponent(value);

    // Replace potentially problematic characters
    return decodedValue
      .replace(/[\u0000-\u001F\u007F-\u009F]/g, '') // Remove control characters
      .replace(/[\\'"<>&]/g, (char) => {
        // Encode HTML special chars to prevent XSS when used in HTML context
        switch (char) {
          case '\\':
            return '\\\\'; // Escape backslash
          case "'":
            return "\\'"; // Escape single quote
          case '"':
            return '\\"'; // Escape double quote
          case '<':
            return '&lt;'; // Encode less than
          case '>':
            return '&gt;'; // Encode greater than
          case '&':
            return '&amp;'; // Encode ampersand
          default:
            return char;
        }
      })
      .trim(); // Remove leading/trailing whitespace
  } catch (error) {
    // If decoding fails, return the original value with basic sanitation
    return value.replace(/[\u0000-\u001F\u007F-\u009F]/g, '').trim();
  }
}

/**
 * Captures UTM parameters from the URL and stores them in localStorage
 * Call this function when your app initializes
 */
export function captureUrlParams() {
  try {
    // Only run in browser environment
    if (typeof window === 'undefined') return;

    // Get URL search params
    const urlParams = new URLSearchParams(window.location.search);
    const params = {};
    let hasUtmParams = false;

    // Extract UTM parameters from URL
    URL_KEYS.forEach((key) => {
      if (urlParams.has(key)) {
        params[key] = sanitizeParamValue(urlParams.get(key));
        hasUtmParams = true;
      }
    });
    // If UTM parameters exist in the URL, store them
    if (hasUtmParams) {
      localStorage.setItem('mtg_utm_params', JSON.stringify(params));
      return params;
    }
    // If no UTM parameters in URL, check localStorage
    return getUtmParams();
  } catch (error) {
    console.error('Error capturing UTM params:', error);
    return {};
  }
}

/**
 * Retrieves stored UTM parameters from localStorage
 */
export function getUtmParams() {
  try {
    // Only run in browser environment
    if (typeof window === 'undefined') return {};

    const storedParams = localStorage.getItem('mtg_utm_params');
    if (!storedParams) return {};
    const parsedParams = JSON.parse(storedParams);

    const sanitizedParams = {};
    Object.keys(parsedParams).forEach((key) => {
      sanitizedParams[key] = sanitizeParamValue(parsedParams[key]);
    });

    return sanitizedParams;
  } catch (error) {
    console.error('Error retrieving UTM params:', error);
    return {};
  }
}
